import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAddToCart, useCart, useProduct } from '../hooks/api-hooks';
import { ErrorState, Loading } from '../components/States';
import { formatDuration, formatPrice } from '../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import type { Lesson, Playback } from '../lib/types';
import { resolveMediaUrl } from '../lib/media-url';

export function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const product = useProduct(slug);
  const cart = useCart();
  const addToCart = useAddToCart();
  const [previewLesson, setPreviewLesson] = useState<Lesson | null>(null);
  const [preview, setPreview] = useState<Playback | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const previewRequestId = useRef(0);

  if (product.isPending) return <Loading />;
  if (product.error) return <ErrorState error={product.error} />;
  if (!product.data) return null;

  const item = product.data;
  const inCart = cart.data?.items.some((entry) => entry.product.id === item.id) ?? false;
  const lessons = item.course?.sections.flatMap((section) => section.lessons) ?? [];
  const previewLessons = lessons.filter((entry) => entry.isPreview);

  function handleAdd() {
    if (!session) {
      navigate('/dang-nhap', { state: { from: `/san-pham/${slug}` } });
      return;
    }
    addToCart.mutate(item.id);
  }

  async function handlePreview(entry: Lesson) {
    if (!item.course) return;
    const requestId = ++previewRequestId.current;
    setPreviewLesson(entry);
    setPreview(null);
    setPreviewError(null);
    setLoadingPreview(true);
    try {
      const result = await api.previewPlayback(item.course.id, entry.id);
      if (requestId === previewRequestId.current) setPreview(result);
    } catch (error: unknown) {
      if (requestId === previewRequestId.current) setPreviewError(error);
    } finally {
      if (requestId === previewRequestId.current) setLoadingPreview(false);
    }
  }

  return (
    <article className="product-detail">
      <header className="product-hero">
        <div className="product-hero-copy">
          <h1>{item.title}</h1>
          {item.shortDescription && <p className="lead">{item.shortDescription}</p>}
          {item.course && (
            <dl className="product-meta" aria-label="Thông tin khoá học">
              <div>
                <dt>Số bài</dt>
                <dd>{lessons.length}</dd>
              </div>
              {item.course.estimatedMinutes !== null && (
                <div>
                  <dt>Thời lượng</dt>
                  <dd>{formatDuration(item.course.estimatedMinutes * 60)}</dd>
                </div>
              )}
              {item.course.level && (
                <div>
                  <dt>Trình độ</dt>
                  <dd>{item.course.level}</dd>
                </div>
              )}
            </dl>
          )}
          <div className="product-purchase">
            <p className="price">{formatPrice(item.price)}</p>
            {inCart ? (
              <button type="button" className="btn btn-quiet" onClick={() => navigate('/gio-hang')}>
                Xem giỏ hàng
              </button>
            ) : (
              <button type="button" className="btn" onClick={handleAdd} disabled={addToCart.isPending}>
                {addToCart.isPending ? 'Đang thêm…' : 'Thêm vào giỏ'}
              </button>
            )}
          </div>
          {addToCart.error && <ErrorState error={addToCart.error} />}
        </div>
        {item.thumbnailUrl && (
          <img src={resolveMediaUrl(item.thumbnailUrl)} alt={`Ảnh giới thiệu ${item.title}`} className="hero-image" />
        )}
      </header>

      {item.description && (
        <section className="product-description">
          <h2>Giới thiệu</h2>
          <p>{item.description}</p>
        </section>
      )}

      {item.course && previewLessons.length > 0 && (
        <section className="course-preview" aria-labelledby="preview-title">
          <div>
            <h2 id="preview-title">Học thử trước khi quyết định</h2>
            <ul className="list preview-list">
              {previewLessons.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="link-button"
                    aria-current={previewLesson?.id === entry.id ? 'true' : undefined}
                    onClick={() => void handlePreview(entry)}
                  >
                    {entry.title}
                  </button>
                  <span className="muted">{formatDuration(entry.durationSeconds)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="preview-stage" aria-live="polite">
            {!previewLesson && <p className="notice">Chọn một bài học thử để xem ngay.</p>}
            {loadingPreview && <Loading label="Đang chuẩn bị bài học thử…" />}
            {previewError !== null && <ErrorState error={previewError} />}
            {preview && previewLesson && (
              <>
                <h3>{previewLesson.title}</h3>
                <iframe
                  title={`Bài học thử: ${previewLesson.title}`}
                  src={preview.iframeUrl}
                  allow="accelerometer; gyroscope; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="video"
                />
              </>
            )}
          </div>
        </section>
      )}

      {item.course && (
        <section className="course-curriculum" aria-labelledby="curriculum-title">
          <h2 id="curriculum-title">Nội dung khoá học</h2>
          <p className="section-lead">
            {item.course.sections.length} chương · {lessons.length} bài học
          </p>
          {item.course.sections.map((section) => (
            <section key={section.id} className="curriculum-section">
              <h3>{section.title}</h3>
              <ol className="curriculum-lessons">
                {section.lessons.map((lesson) => (
                  <li key={lesson.id}>
                    <span>{lesson.title}</span>
                    <span className="lesson-meta">
                      {lesson.isPreview && (
                        <button type="button" className="link-accent" onClick={() => void handlePreview(lesson)}>
                          Học thử
                        </button>
                      )}
                      <span className="muted">{formatDuration(lesson.durationSeconds)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </section>
      )}
    </article>
  );
}
